import { WhitelabelType } from '@/Enums/Whitelabels/WhitelabelType.enum';
import { IAdvertiser } from '@/Interfaces/Advertiser.interface';
import {
  IClientControlOption,
  IControlOption,
  IOrganizationControlOption,
  IWhitelabelControlOption,
} from '@/Interfaces/ControlOption.interface';
import {
  IFormattedWhitelabelsResponse,
  IParsedWhitelabel,
  IParsedWhitelabelWithRelations,
  IWhitelabel,
  IWhitelabelWithRelations,
} from '@/Interfaces/Whitelabels/Whitelabel.interface';
import { getSortFilter } from '@/helpers/grid/getSortFilter';
import type { SearchWhitelabelsFilters, SearchWhitelabelsPayload } from '@/types/whitelabels.types';
import { formatClientsResponseData } from '../clients';

export const formatWhitelabel = (whitelabel: IWhitelabel, parentWhitelabel?: IWhitelabel): IParsedWhitelabel => {
  return {
    id: whitelabel.id,
    name: whitelabel.name,
    organization: parentWhitelabel
      ? { label: parentWhitelabel?.name, value: parentWhitelabel?.id }
      : { label: whitelabel.name, value: whitelabel.id },
    parentId: parentWhitelabel?.id ?? null,
    createdAt: whitelabel.created_at,
    sidebarBg: whitelabel.sidebar_bg ?? '',
    loginBg: whitelabel.login_bg ?? '',
    buttonBg: whitelabel.button_bg ?? '',
    sidebarTextColor: whitelabel.sidebar_text_color,
    loginTextDark: !!whitelabel.login_text_dark,
    favicon: whitelabel.favicon ?? '',
    domain: whitelabel.domain ?? '',
    domainExternal: whitelabel.domain_external ?? '',
    mainLogo: whitelabel.main_logo ?? '',
    sidebarLogo: whitelabel.sidebar_logo ?? '',
    type: parentWhitelabel ? WhitelabelType.Whitelabel : WhitelabelType.Organization,
    requiresCampaignApproval: whitelabel.requires_campaign_approval ?? false,
    forecastByPub: whitelabel.forecast_by_pub,
    extendedAudiences: whitelabel.extended_audiences,
    extendedReportingDashboardFilters: whitelabel.extended_reporting_dashboard_filters,
    showLineItemAlternativeId: whitelabel.show_line_item_alternative_id,
    showCampaignAlternativeId: whitelabel.show_campaign_alternative_id,
    showAdvertiserAlternativeId: whitelabel.show_advertiser_alternative_id,
    settings: whitelabel.settings,
    domainStatus: whitelabel.domain_status,
    domainExternalStatus: whitelabel.domain_external_status,
    domainVerificationRecord: whitelabel.domain_verification_record,
    billingModel: whitelabel.billing_model,
    visibleReportingScreenTabs: whitelabel.visible_reporting_screen_tabs,
    visibleReportBuilderBeeswaxDimensions: whitelabel.visible_report_builder_beeswax_dimensions,
    showClientSpendInReportingDashboards: whitelabel.show_client_spend_in_reporting_dashboards,
    showReachInForecaster: whitelabel.show_reach_in_forecaster,
    visibleForecasterChannels: whitelabel.visible_forecaster_channels,
    beeswaxImpressionsByTypeTableTabs: whitelabel.beeswax_impressions_by_type_table_tabs,
  };
};

export const formatWhitelabelsResponse = (whitelabels: IWhitelabel[]): IFormattedWhitelabelsResponse => {
  const orgs: IOrganizationControlOption[] = [];
  const parsedWhitelabels: IParsedWhitelabel[] = [];

  const orgById = new Map<number, IWhitelabel>();
  whitelabels.forEach(w => {
    if (w.parent_id == null) orgById.set(w.id, w);
  });

  whitelabels.forEach(whitelabel => {
    const parent = whitelabel.parent_id != null ? orgById.get(whitelabel.parent_id) : undefined;

    const formatted = formatWhitelabel(whitelabel, parent);
    parsedWhitelabels.push(formatted);

    if (!parent) {
      orgs.push({ value: formatted.id, label: formatted.name, domainExternal: formatted.domainExternal });
    }

    if (whitelabel.children?.length) {
      parsedWhitelabels.push(
        ...whitelabel.children.map(childWhitelabel => formatWhitelabel(childWhitelabel, whitelabel)),
      );
    }
  });

  return { whitelabels: parsedWhitelabels, orgs };
};

export function parseWhitelabelsWithRelations<AType = IControlOption>(
  responseWhitelabels: IWhitelabelWithRelations[],
  advertiserMapper?: (advertiser: IAdvertiser) => AType,
  advertiserFilter?: ((record: IAdvertiser, i?: number, arr?: IAdvertiser[]) => boolean) | null,
  includeEmpty: boolean = false,
): IParsedWhitelabelWithRelations<AType>[] {
  const result: IParsedWhitelabelWithRelations<AType>[] = [];
  responseWhitelabels.forEach(whitelabel => {
    const clients = formatClientsResponseData(
      whitelabel.clients,
      advertiserMapper,
      advertiserFilter,
      includeEmpty,
    ) as IClientControlOption<AType>[];
    if (!clients?.length && !includeEmpty) {
      return;
    }
    result.push({
      id: whitelabel.id,
      name: whitelabel.name,
      parentId: whitelabel.parent_id,
      clients,
      type: whitelabel.parent_id ? WhitelabelType.Whitelabel : WhitelabelType.Organization,
      requiresCampaignApproval: whitelabel.requires_campaign_approval ?? false,
    });
  });
  return result;
}

export const mapWhitelabelToControlOption = (whitelabel: IParsedWhitelabelWithRelations): IWhitelabelControlOption => ({
  value: whitelabel.id,
  label: whitelabel.name,
  clients: whitelabel.clients || [],
});

export const transformWhitelabelsFilters = (filters: SearchWhitelabelsFilters): SearchWhitelabelsPayload => {
  const { page, limit, search, sort, organizationIds } = filters;
  return {
    page,
    limit,
    search,
    sort: getSortFilter(sort),
    loadRelations: true,
    organizationIds,
  };
};
